import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import OnboardingTutorial, { useOnboarding } from "@/components/OnboardingTutorial";
import useRealtimeOrders from "@/hooks/useRealtimeOrders";
import CriticalOrderOverlay from "@/components/CriticalOrderOverlay";
import { getAlarmCtx } from "@/utils/alarmAudio";
import { CourierPermissionsOnboarding } from "@/components/courier/CourierPermissionsOnboarding";
import AboutArteknosModal from "@/components/AboutArteknosModal";
import useAboutOnce from "@/hooks/useAboutOnce";

import { CourierHeader } from "@/components/courier/CourierHeader";
import { DriverStatusCard } from "@/components/courier/DriverStatusCard";
import {
  ActiveDeliveriesBanner,
  LowCreditBanner,
} from "@/components/courier/ActiveDeliveriesBanner";
import { BroadcastList } from "@/components/courier/BroadcastList";
import {
  LeaderboardCard,
  HotZonesList,
  StatsGrid,
} from "@/components/courier/CourierWidgets";
import { AvailableOrdersList } from "@/components/courier/AvailableOrdersList";
import { MyDeliveriesList } from "@/components/courier/MyDeliveriesList";
import { WalletRechargeModal } from "@/components/courier/WalletRechargeModal";
import CommissionStatementCard from "@/components/courier/CommissionStatementCard";
import { CourierMapPanel } from "@/components/map/CourierMapPanel";
import { WalletWaveCard } from "@/components/wallet/WalletWaveCard";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useAdaptiveTracking } from "@/hooks/useAdaptiveTracking";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Loud distinctive alarm — used for new order broadcasts.
 */
const playAlarmSound = () => {
  try {
    const audioCtx = getAlarmCtx();
    if (!audioCtx) return;
    const now = audioCtx.currentTime;
    const playTone = (freq, start, duration, type = "square", volume = 0.5) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.frequency.value = freq;
      osc.type = type;
      gain.gain.setValueAtTime(volume, start);
      gain.gain.exponentialRampToValueAtTime(0.01, start + duration);
      osc.start(start);
      osc.stop(start + duration);
    };
    for (let i = 0; i < 3; i++) {
      const offset = i * 0.6;
      playTone(600, now + offset, 0.3, "sawtooth", 0.4);
      playTone(900, now + offset + 0.3, 0.3, "sawtooth", 0.4);
    }
    for (let i = 0; i < 5; i++) {
      playTone(1200, now + 1.8 + i * 0.12, 0.08, "square", 0.5);
    }
  } catch (e) {
    console.error("Audio error:", e);
  }
};

const scrollToMyOrders = () => {
  const el = document.querySelector('[data-testid="active-course-list"]') || document.querySelector('[data-testid="my-orders-list"]');
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function CourierDashboard({ user, onLogout, onUserUpdate }) {
  const navigate = useNavigate();
  const [availableOrders, setAvailableOrders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [isAvailable, setIsAvailable] = useState(user?.is_available ?? true);
  const [walletBalance, setWalletBalance] = useState(user?.wallet_balance ?? 0);
  const [stats, setStats] = useState({ totalDeliveries: 0, rating: 5.0 });
  const [creditBalance, setCreditBalance] = useState(0);
  const [walletRefresh, setWalletRefresh] = useState(0);
  const [prevOrderCount, setPrevOrderCount] = useState(null);
  const [ratingOrderId, setRatingOrderId] = useState(null);
  const [ratingScore, setRatingScore] = useState(0);
  const [ratedOrders, setRatedOrders] = useState({});
  const [myRank, setMyRank] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  // Actions ACCEPTER / REFUSER depuis la notification (écran verrouillé)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const oid = p.get("broadcast");
    const action = p.get("action");
    if (!oid || !user?.id) return;
    if (!action) {
      // Tap sur la notification : rouvrir l'alerte plein écran de cette course si elle est encore ouverte
      axios.get(`${API}/orders/open-broadcasts`, { params: { courier_id: user.id } })
        .then(({ data }) => { const b = data.find((x) => x.order_id === oid); if (b) setCriticalOrder(b); })
        .catch(() => {});
      window.history.replaceState(null, "", "/courier");
      return;
    }
    window.history.replaceState(null, "", "/courier");
    const call = action === "accept"
      ? axios.post(`${API}/orders/${oid}/accept-broadcast`, { courier_id: user.id })
      : axios.post(`${API}/orders/${oid}/decline-broadcast`);
    call.then(() => {
      toast.success(action === "accept" ? "Course acceptée ! Direction le point de retrait." : "Course refusée");
      if (action === "accept") showAccepted(oid);
    })
      .catch((e) => toast.error(e.response?.status === 409 ? "Trop tard, un autre livreur a pris cette course" : e.response?.data?.detail || "Action impossible"));
  }, [user?.id]);

  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState(2000);
  const [criticalOrder, setCriticalOrder] = useState(null);
  const { showOnboarding, completeOnboarding } = useOnboarding(user?.role);
  const aboutModal = useAboutOnce(user?.id);

  const activeOrders = myOrders.filter(
    (o) => !["delivered", "cancelled"].includes(o.status)
  );
  const userInitials = (user?.full_name || "A")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  // ============ DATA FETCHING ============
  const fetchMyRank = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/leaderboard/me/${user.id}?period=week`);
      setMyRank(res.data);
    } catch (err) {
      // 404 = livreur pas encore seedé ; pas un vrai problème → silencieux
      if (err?.response?.status !== 404) {
        console.warn("Failed to fetch leaderboard rank:", err);
      }
    }
  }, [user?.id]);

  const fetchCreditBalance = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API}/credits/balance/${user.id}`);
      setCreditBalance(response.data.credit_balance || 0);
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.warn("Error fetching credits:", error);
      }
    }
  }, [user?.id]);

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    try {
      // === Auto-rebind a CHAQUE fetch (no-op cote backend si rien a rebinder)
      // Indispensable pour resoudre le bug "client voit livreur assigne mais
      // le livreur ne voit rien sur son dashboard" (cas comptes re-inscrits / IDs stale).
      if (user.phone || user.email) {
        try {
          const rebindUrl = new URL(`${API}/orders/courier/rebind`);
          rebindUrl.searchParams.set("courier_id", user.id);
          if (user.phone) rebindUrl.searchParams.set("phone", user.phone);
          if (user.email) rebindUrl.searchParams.set("email", user.email);
          const r = await axios.post(rebindUrl.toString());
          if (r.data?.rebind_count > 0) {
            toast.success(`${r.data.rebind_count} commande(s) recuperee(s) sur votre compte !`);
          }
        } catch (rebindErr) {
          // Best-effort : silencieux
        }
      }

      const myOrdersUrl = new URL(`${API}/orders`);
      myOrdersUrl.searchParams.set("role", "courier");
      myOrdersUrl.searchParams.set("courier_id", user.id);
      if (user.phone) myOrdersUrl.searchParams.set("courier_phone", user.phone);
      if (user.email) myOrdersUrl.searchParams.set("courier_email", user.email);
      myOrdersUrl.searchParams.set("limit", "50");

      const [availableRes, myOrdersRes, profileRes, openBcRes] = await Promise.all([
        axios.get(`${API}/orders/available`).catch(() => ({ data: [] })),
        axios.get(myOrdersUrl.toString()).catch((e) => { console.warn("my orders:", e?.response?.status); return null; }),
        axios.get(`${API}/users/${user.id}`).catch(() => ({ data: {} })),
        axios.get(`${API}/orders/open-broadcasts`, { params: { courier_id: user.id } }).catch(() => ({ data: [] })),
      ]);
      setBroadcasts((prev) => {
        const fresh = new Map(openBcRes.data.map((b) => [b.order_id, b]));
        // garde les détails WS (priorité, expiration) des courses encore ouvertes côté serveur
        prev.forEach((b) => fresh.has(b.order_id) && fresh.set(b.order_id, { ...fresh.get(b.order_id), ...b }));
        return [...fresh.values()].slice(0, 5);
      });
      setAvailableOrders(availableRes.data);
      setPrevOrderCount((prev) => {
        if (prev !== null && availableRes.data.length > prev) {
          playAlarmSound();
          toast.info("Nouvelle commande disponible !", { duration: 5000 });
        }
        return availableRes.data.length;
      });
      if (myOrdersRes) setMyOrders(myOrdersRes.data);
      setWalletBalance(profileRes.data.wallet_balance ?? walletBalance);
      setStats({
        totalDeliveries: profileRes.data.total_deliveries ?? 0,
        rating: profileRes.data.rating ?? 5.0,
      });
      if (typeof profileRes.data.is_available === "boolean") {
        setIsAvailable(profileRes.data.is_available);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [user?.id, user?.email, user?.phone]);

  // ============ GPS ADAPTATIF (batterie) ============
  const hasActiveCourse = activeOrders.length > 0;
  const geo = useGeolocation({ watch: true, enabled: isAvailable, mode: hasActiveCourse ? "active" : "idle" });
  const tracking = useAdaptiveTracking({
    courierId: user?.id,
    position: geo.position,
    online: isAvailable,
    activeCourse: hasActiveCourse,
  });

  useEffect(() => {
    if (!user?.is_approved) {
      toast.error("Votre compte n'est pas encore approuvé par l'admin");
    }
    fetchData();
    fetchCreditBalance();
    fetchMyRank();
    const dataInterval = setInterval(fetchData, 5000);
    return () => clearInterval(dataInterval);
  }, [user?.is_approved, fetchData, fetchCreditBalance, fetchMyRank]);

  // ============ REALTIME WS EVENTS ============
  useRealtimeOrders(user?.id, (event, data) => {
    if (event === "courier_assigned" || event === "order_status_changed") {
      if (event === "courier_assigned") {
        try { playAlarmSound(); } catch (err) { console.warn("Alarm sound error:", err); }
        toast.success(`Nouvelle commande #${(data?.order_id || "").substring(0, 8)} !`);
      }
      fetchData();
    }
    if (event === "order_assigned" && data?.order_id) {
      try { playAlarmSound(); } catch (err) { console.warn("Alarm sound error:", err); }
      setCriticalOrder((cur) => (cur ? cur : data));
      fetchData();
    }
    if (event === "order_broadcast" && data?.order_id) {
      try { playAlarmSound(); } catch (err) { console.warn("Alarm sound error:", err); }
      setBroadcasts((prev) => {
        if (prev.some((b) => b.order_id === data.order_id)) return prev;
        return [data, ...prev].slice(0, 5);
      });
      setCriticalOrder((cur) => (cur ? cur : data));
      toast.message(`📣 Nouvelle course disponible · ${data.pickup_address || ""}`);
    }
    if (event === "broadcast_closed" && data?.order_id) {
      setBroadcasts((prev) => prev.filter((b) => b.order_id !== data.order_id));
      setCriticalOrder((cur) => (cur?.order_id === data.order_id ? null : cur));
    }
    if (event === "wallet_topup" && typeof data?.new_balance === "number") {
      setWalletBalance(data.new_balance);
      toast.success(`Wallet rechargé : +${data.amount} FCFA`);
    }
  });

  // ============ HANDLERS ============
  const toggleAvailability = async () => {
    // STOP pendant une course : termine les courses en cours (commission 10 %) puis passe hors ligne
    const running = isAvailable ? activeOrders.filter((o) => ["assigned", "picked_up", "in_transit"].includes(o.status)) : [];
    if (running.length) {
      const label = running.length === 1 ? `la course #${running[0].id.slice(0, 8)}` : `vos ${running.length} courses en cours`;
      if (!window.confirm(`Terminer ${label} ? Elle(s) sera(ont) marquée(s) comme livrée(s).`)) return;
      try {
        let total = 0;
        for (const o of running) {
          const { data } = await axios.put(`${API}/orders/${o.id}/deliver?courier_id=${user.id}`);
          total += data.commission || 0;
        }
        toast.success(`Course terminée${total ? ` · commission ARTEKNOS ${total} FCFA prélevée` : ""}`);
        fetchData();
        fetchCreditBalance();
        setWalletRefresh((n) => n + 1);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Impossible de terminer la course");
        fetchData();
        return;
      }
    }
    try {
      await axios.put(`${API}/couriers/${user.id}/availability?is_available=${!isAvailable}`);
      setIsAvailable(!isAvailable);
      toast.success(
        isAvailable ? "Vous êtes maintenant hors ligne" : "Vous êtes maintenant disponible"
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement de disponibilité");
    }
  };

  // Après acceptation : affiche tout de suite la course en haut du tableau de bord
  const showAccepted = async (orderId) => {
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}`);
      if (data?.id) setMyOrders((prev) => [data, ...prev.filter((o) => o.id !== data.id)]);
    } catch (e) {
      console.warn("accepted order fetch:", e?.response?.status);
    }
    await fetchData();
    setTimeout(scrollToMyOrders, 150);
  };

  const acceptBroadcast = async (orderId) => {
    try {
      await axios.post(`${API}/orders/${orderId}/accept-broadcast`, { courier_id: user.id });
      setBroadcasts((prev) => prev.filter((b) => b.order_id !== orderId));
      toast.success("Course attribuée ! Direction le point de retrait.");
      showAccepted(orderId);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("Trop tard, un autre livreur a pris cette course");
        setBroadcasts((prev) => prev.filter((b) => b.order_id !== orderId));
      } else {
        toast.error(error.response?.data?.detail || "Erreur");
      }
    }
  };

  const acceptOrder = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/accept?courier_id=${user.id}`);
      toast.success("Commande acceptée!");
      showAccepted(orderId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'acceptation");
    }
  };

  const advanceOrder = async (orderId, nextStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/advance?actor_role=courier`);
      const msg = {
        picked_up: "Colis récupéré !",
        in_transit: "En route !",
        delivered: "Livraison terminée ! Merci !",
      };
      toast.success(msg[nextStatus] || "Statut mis à jour");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const submitRecharge = async (amount) => {
    try {
      const r = await axios.post(`${API}/couriers/${user.id}/wallet/recharge`, {
        courier_id: user.id,
        amount: parseInt(amount, 10),
        phone_number: user.phone,
      });
      if (r.data?.redirect_url) {
        window.location.href = r.data.redirect_url;
      } else {
        toast.success("Recharge initiée");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'initiation");
    }
  };

  const submitClientRating = async (order) => {
    try {
      await axios.post(`${API}/ratings`, {
        order_id: order.id,
        rater_id: user.id,
        rated_id: order.customer_id,
        rater_role: "courier",
        score: ratingScore,
      });
      toast.success("Client noté !");
      setRatedOrders({ ...ratedOrders, [order.id]: true });
      setRatingOrderId(null);
      setRatingScore(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // ============ RENDER ============
  if (!user?.is_approved) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-yellow-600">⏳ En attente de validation</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground mb-4">
              Votre compte livreur est en cours de validation par l'administrateur.
              Vous recevrez une notification une fois approuvé.
            </p>
            <Button onClick={onLogout} variant="outline">
              Se déconnecter
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] text-white pb-32">
      <CourierPermissionsOnboarding />
      <CriticalOrderOverlay
        order={criticalOrder}
        onDecline={async (o) => {
          setBroadcasts((prev) => prev.filter((b) => b.order_id !== o.order_id));
          setCriticalOrder(null);
          try {
            await axios.post(`${API}/orders/${o.order_id}/decline-broadcast`);
            toast("Course refusée — elle est proposée à un autre livreur");
          } catch (err) {
            toast.error(err.response?.data?.detail || "Refus non enregistré");
          }
        }}
        onAccept={async (o) => {
          try {
            await axios.post(`${API}/orders/${o.order_id}/accept-broadcast`, {
              courier_id: user.id,
            });
            toast.success("Course acceptée ! Direction le point de retrait.");
            setBroadcasts((prev) => prev.filter((b) => b.order_id !== o.order_id));
            setCriticalOrder(null);
            showAccepted(o.order_id);
          } catch (err) {
            if (err.response?.status === 409) {
              toast.error("Trop tard, un autre livreur a pris cette course");
            } else {
              toast.error(err.response?.data?.detail || "Erreur d'acceptation");
            }
            setCriticalOrder(null);
          }
        }}
        onExpire={(o) => {
          toast.warning("Course expirée — proposée au livreur suivant");
          setBroadcasts((prev) => prev.filter((b) => b.order_id !== o.order_id));
          setCriticalOrder(null);
        }}
        onDismiss={() => setCriticalOrder(null)}
      />

      <AboutArteknosModal open={aboutModal.open} onClose={aboutModal.close} />

      {showOnboarding && (
        <OnboardingTutorial
          userRole="courier"
          userName={user?.full_name}
          onComplete={completeOnboarding}
        />
      )}

      <CourierHeader
        user={user}
        onUserUpdate={onUserUpdate}
        isAvailable={isAvailable}
        activeCount={activeOrders.length}
        rating={stats.rating}
        userInitials={userInitials}
        onScrollToOrders={scrollToMyOrders}
        onOpenAbout={aboutModal.openManually}
        onOpenProfile={() => navigate("/courier/profile")}
        onLogout={onLogout}
      />

      <div className="px-4 md:px-8 py-6">
        <div className="max-w-5xl mx-auto space-y-6">
          <DriverStatusCard
            isAvailable={isAvailable}
            onToggleAvailability={toggleAvailability}
            walletBalance={walletBalance}
            totalDeliveries={stats.totalDeliveries}
            onOpenWalletRecharge={() => setShowRechargeModal(true)}
          />

          {activeOrders.length > 0 && (
            <MyDeliveriesList
              title="Course en cours"
              testId="active-course-list"
              myOrders={activeOrders}
              onRefresh={fetchData}
              ratedOrders={ratedOrders}
              ratingOrderId={ratingOrderId}
              ratingScore={ratingScore}
              onAdvance={advanceOrder}
              onStartRating={setRatingOrderId}
              onSetScore={setRatingScore}
              onSubmitRating={submitClientRating}
              onCancelRating={() => {
                setRatingOrderId(null);
                setRatingScore(0);
              }}
            />
          )}

          <WalletWaveCard key={walletRefresh} user={user} />

          <CourierMapPanel
            user={user}
            position={geo.position}
            status={geo.status}
            onRequest={geo.request}
            online={isAvailable}
            activeCourse={hasActiveCourse}
            intervalMs={tracking.intervalMs}
            lowBattery={tracking.lowBattery}
          />

          <LowCreditBanner
            creditBalance={creditBalance}
            onRecharge={() => navigate("/credits")}
          />

          <ActiveDeliveriesBanner
            activeOrders={activeOrders}
            onScrollToOrders={scrollToMyOrders}
          />

          <BroadcastList
            broadcasts={broadcasts}
            onAccept={acceptBroadcast}
            visible={isAvailable}
          />

          <LeaderboardCard myRank={myRank} onClick={() => navigate("/leaderboard")} />

          <CommissionStatementCard courierId={user?.id} />

          <HotZonesList visible={isAvailable} />

          <StatsGrid
            activeCount={activeOrders.length}
            totalDeliveries={stats.totalDeliveries}
            creditBalance={creditBalance}
            rating={stats.rating}
          />

          <AvailableOrdersList
            orders={availableOrders}
            onAccept={acceptOrder}
            visible={isAvailable}
          />

          <MyDeliveriesList
            title="Historique de mes livraisons"
            myOrders={myOrders.filter((o) => ["delivered", "cancelled"].includes(o.status))}
            onRefresh={fetchData}
            ratedOrders={ratedOrders}
            ratingOrderId={ratingOrderId}
            ratingScore={ratingScore}
            onAdvance={advanceOrder}
            onStartRating={setRatingOrderId}
            onSetScore={setRatingScore}
            onSubmitRating={submitClientRating}
            onCancelRating={() => {
              setRatingOrderId(null);
              setRatingScore(0);
            }}
          />
        </div>
      </div>

      <WalletRechargeModal
        open={showRechargeModal}
        walletBalance={walletBalance}
        rechargeAmount={rechargeAmount}
        onChangeAmount={setRechargeAmount}
        onClose={() => setShowRechargeModal(false)}
        onSubmit={submitRecharge}
      />
    </div>
  );
}
